import { Link } from "react-router-dom";
import { Reveal } from "@/components/Reveal";
import { PropertyCard } from "@/components/PropertyCard";
import { UNITS } from "@/data/units";
import { usePublishedListings, usePhotoOverrides } from "@/data/useListings";

const MONTHLY_RATES = [
  { tier: "Shared Bath", price: "From $1,190", note: "Comfortable furnished private rooms with a shared bathroom." },
  { tier: "Private Bath / Master", price: "From $1,450", note: "Furnished private rooms with a private or master bathroom." },
  { tier: "Premium Ensuite", price: "From $1,600", note: "Spacious ensuite rooms with maximum privacy and comfort." },
  { tier: "Private Studio Loft", price: "From $1,980", note: "An entire private second-floor loft with its own bedroom, bath, kitchenette, and living area." },
];

export default function Properties() {
  const extra = usePublishedListings("furnished_room");
  const overrides = usePhotoOverrides();
  const all = [...UNITS.map((u) => (overrides[u.slug] ? { ...u, cover: overrides[u.slug].cover, gallery: overrides[u.slug].gallery } : u)), ...(extra || [])];
  return (
    <section data-testid="properties-page" className="px-6 md:px-12 pt-36 md:pt-40 pb-24 md:pb-32">
      <div className="max-w-7xl mx-auto">
        <Reveal className="max-w-3xl">
          <span className="text-xs sm:text-sm tracking-[0.2em] uppercase font-medium text-[#8A9A86]">Furnished Rooms</span>
          <h1 className="mt-5 text-5xl sm:text-6xl tracking-tighter font-light leading-none text-[#1C1917]">Available units</h1>
          <p className="mt-8 text-lg text-[#44403C] leading-relaxed font-light">
            {UNITS.length} professionally managed, furnished private rooms within shared homes
            across the Austin area — including our newest units in Round Rock. Designed for
            extended stays by professionals, interns, travel nurses, engineers, and relocating guests.
          </p>
        </Reveal>

        <Reveal delay={0.1}>
          <div data-testid="rooms-banner" className="mt-10 flex items-center gap-3 rounded-2xl border border-[#E7E5E4] bg-[#F5F5F4] px-5 py-4 w-full sm:w-fit">
            <span className="w-2 h-2 rounded-full bg-[#8A9A86] shrink-0" />
            <span className="text-sm text-[#44403C]">
              Each listing is a private furnished room within a shared, professionally managed Masaya home — not an entire house.
            </span>
          </div>
        </Reveal>

        {/* Monthly accommodation rates (merged from long-term stays) */}
        <Reveal delay={0.15}>
          <div className="mt-14" data-testid="monthly-rates">
            <div className="flex items-end justify-between flex-wrap gap-4 mb-6">
              <div>
                <span className="text-xs sm:text-sm tracking-[0.2em] uppercase font-medium text-[#8A9A86]">Monthly stays — 30+ nights</span>
                <h2 className="mt-2 text-2xl sm:text-3xl tracking-tight font-light text-[#1C1917]">Starting monthly rates</h2>
              </div>
              <Link to="/long-term-stays" data-testid="monthly-inquiry-link" className="text-sm text-[#44403C] hover:text-[#1C1917] underline underline-offset-4">
                Submit a long-term inquiry →
              </Link>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-px bg-[#E7E5E4] border border-[#E7E5E4] rounded-2xl overflow-hidden">
              {MONTHLY_RATES.map((r) => (
                <div key={r.tier} className="bg-[#FAFAF9] p-6 md:p-7" data-testid={`rate-${r.tier.toLowerCase().replace(/[^\w]+/g, "-")}`}>
                  <p className="text-xs tracking-[0.18em] uppercase font-medium text-[#8A9A86]">{r.tier}</p>
                  <p className="mt-3 text-2xl font-normal tracking-tight text-[#1C1917]">{r.price}<span className="text-sm text-[#78716C] font-light">/mo</span></p>
                  <p className="mt-2 text-xs text-[#44403C] font-light leading-relaxed">{r.note}</p>
                </div>
              ))}
            </div>
            <p className="mt-4 text-xs text-[#78716C] font-light max-w-2xl">
              Rates vary by home, season, and length of stay. All monthly stays include utilities,
              Wi-Fi, housekeeping, and furnished common areas. Prices subject to availability.
            </p>
          </div>
        </Reveal>

        <div className="mt-16 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
          {all.map((u, i) => (
            <Reveal key={u.slug} delay={(i % 3) * 0.06}>
              <PropertyCard property={u} index={i} />
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  );
}
